import { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { styled } from "@mui/material/styles";
import { FunnelIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { RadioInput } from "components/forms";

import { useHistoryStore } from "../historyStore";

const Title = styled(DialogTitle)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Content = styled(DialogContent)(({ theme }) => ({
  maxWidth: "100%",
  width: "350px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
}));

const advancedFiltersSchema = z.object({
  timeInBed: z.string(),
  timeAsleep: z.string(),
});

type AdvancedFiltersSchema = z.infer<typeof advancedFiltersSchema>;

export function AdvancedFiltersDialog() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const timeInBed = useHistoryStore((s) => s.timeInBed);
  const setTimeInBed = useHistoryStore((s) => s.setTimeInBed);

  const timeAsleep = useHistoryStore((s) => s.timeAsleep);
  const setTimeAsleep = useHistoryStore((s) => s.setTimeAsleep);

  const { control, handleSubmit } = useForm<AdvancedFiltersSchema>({
    resolver: zodResolver(advancedFiltersSchema),
    defaultValues: {
      timeInBed: timeInBed.toString(),
      timeAsleep: timeAsleep.toString(),
    },
  });

  const onSubmit = (data: AdvancedFiltersSchema) => {
    setTimeInBed(Number.parseInt(data.timeInBed));
    setTimeAsleep(Number.parseInt(data.timeAsleep));
  };

  const options = [
    { id: "0", label: t("All") },
    { id: "240", label: t("4_hours_or_more") },
    { id: "120", label: t("2_hours_or_more") },
    { id: "60", label: t("1_hour_or_more") },
    { id: "30", label: t("30_minutes_or_more") },
  ];

  return (
    <>
      <Button
        startIcon={<FunnelIcon />}
        color={"info"}
        onClick={() => setIsOpen(true)}
      >
        {t("Advanced filters")}
      </Button>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        component={"form"}
        onChange={handleSubmit(onSubmit)}
      >
        <Title>
          {t("Advanced filters")}
          <IconButton
            size={"small"}
            disableRipple
            color={"accent.soft"}
            onClick={() => setIsOpen(false)}
          >
            <XIcon />
          </IconButton>
        </Title>
        <Content dividers>
          <RadioInput
            fieldName={"timeInBed"}
            label={t("time_in_bed")}
            labelVariant={"headingSmallSemibold"}
            control={control}
            options={options}
          />
          <RadioInput
            fieldName={"timeAsleep"}
            label={t("time_asleep")}
            labelVariant={"headingSmallSemibold"}
            control={control}
            options={options}
          />
          <Typography variant={"bodySmallRegular"} textAlign={"center"}>
            {t("Changes are saved automatically")}
          </Typography>
        </Content>
      </Dialog>
    </>
  );
}
